import format from 'string-template';
import { Collection, getFirestore } from 'firestorter';

import CollectionName from '../../utils/collections';
import { pathPlaceholder } from '../../utils/paths';
import BaseDocument from '../../Model/BaseDocument';
import { ClientDocumentSource } from '../../chat/Model/ClientDocument';

const collectionPath = (productId) => format(CollectionName.LIBRARY_DOCS, {
  [pathPlaceholder.PRODUCT_ID]: productId,
});

/*
  A collection group matches on the collection's own name rather than a path, so the
  group query needs the last segment of the template on its own.
*/
const LIBRARY_DOCS_GROUP = CollectionName.LIBRARY_DOCS.split('/').pop();

/**
 * One document a coach has published to a product's clients —
 * `product/{productId}/libraryDocs/{documentId}`.
 *
 * Lives under the product it belongs to rather than in a collection of its own: the
 * product already is the audience, so nesting keeps the two from drifting apart and
 * a deleted product takes its documents with it.
 *
 * This is the coach's own record of what was published, kept separately from the
 * per-client `clientDocument` records the server fans out from it. The two must not
 * be the same row: a client can have their copy removed, and the coach's list still
 * has to show what the product was given.
 *
 * Publishing one file to several products writes one document each, all pointing at
 * the same uploaded file — so the file is uploaded once, each product owns its own
 * row, and removing it from one leaves the others untouched. `assetPath` is what
 * ties them back together: the upload appends a uuid, so no two publishes share one.
 *
 * Getter names deliberately match `ClientDocument`, so the Chat Documents row and
 * menu render these without a second component.
 */
class LibraryDoc extends BaseDocument {
  /**
   * The product this document was published to.
   *
   * Falls back to the parent of the parent, which is where a collection group
   * result carries it: the query spans every product, so the path is the only
   * place the id is guaranteed to be.
   */
  get productId() {
    return this.data.productId || this.ref?.parent?.parent?.id || '';
  }

  get fileName() {
    return this.data.fileName || '';
  }

  /**
   * Storage path of the uploaded file, not a download URL. Named for ClientDocument.
   *
   * Also the identity of a publish: every record one publish wrote shares it, and
   * the upload appends a uuid so two publishes never collide.
   */
  get assetPath() {
    return this.data.assetPath || '';
  }

  get mimeType() {
    return this.data.mimeType || '';
  }

  get type() {
    return this.data.type || '';
  }

  get fileSize() {
    return this.data.fileSize || 0;
  }

  get uploadedAt() {
    return this.data.uploadedAt?.toDate?.() || null;
  }

  get uploadedByName() {
    return this.data.uploadedByName || '';
  }

  /** Nothing here arrives through a chat message, so an upload is the only source. */
  get source() {
    return this.data.source || ClientDocumentSource.UPLOAD;
  }

  /** The coach's saved-documents flag has no meaning here; these are already curated. */
  get isSaved() {
    return !!this.data.isSaved;
  }

  /**
   * Every document a coach has published, across all their products.
   *
   * One collection-group read rather than a query per product: `coachId` is stored
   * on each document for exactly this, the same way `Product.getProductByCoachId` reads
   * a coach's products.
   *
   * @param {string} coachId - The coach whose documents to load.
   * @returns {Promise<Array<LibraryDoc>>} Documents, newest first.
   */
  static async getAllByCoachId(coachId) {
    const db = getFirestore();
    const collection = new Collection(
      () => db.collectionGroup(LIBRARY_DOCS_GROUP),
      {
        createDocument: (source, options) => new LibraryDoc(source, options),
        query: (ref) => ref.where('coachId', '==', coachId),
      },
    );
    await collection.fetch();

    // Sorted here rather than in the query: ordering would need a composite index on
    // a collection group, and a coach's set is small.
    return [...collection.docs].sort((first, second) => {
      const firstTime = first.uploadedAt?.getTime() ?? 0;
      const secondTime = second.uploadedAt?.getTime() ?? 0;
      return secondTime - firstTime;
    });
  }

  /**
   * Publishes one uploaded file to every selected product.
   *
   * Writing the records is all this does. Fanning them out into each client's
   * Library is the server's job, triggered by these writes — the web app must never
   * write `clientDocument` rows for a whole cohort itself.
   *
   * @param {string} coachId - The coach publishing the document.
   * @param {Array<string>} productIds - The products to publish to.
   * @param {object} file - The uploaded file.
   * @param {string} file.fileName - Display name.
   * @param {string} file.assetPath - Storage path of the uploaded file.
   * @param {string} file.mimeType - Content type as stored.
   * @param {string} file.type - One of FileType, for how the row renders.
   * @param {number} file.fileSize - Size in bytes.
   * @param {string} file.uploadedBy - Uid of the signed-in editor.
   * @param {string} file.uploadedByName - Display name of the signed-in editor.
   * @returns {Promise<void>} Resolves once the writes land.
   */
  static async publish(coachId, productIds, {
    fileName,
    assetPath,
    mimeType,
    type,
    fileSize,
    uploadedBy,
    uploadedByName,
  }) {
    const db = getFirestore();
    const uploadedAt = new Date();

    await Promise.all(productIds.map((productId) => (
      db.collection(collectionPath(productId)).add({
        coachId,
        productId,
        fileName,
        assetPath,
        mimeType,
        type,
        fileSize,
        uploadedBy,
        uploadedByName,
        uploadedAt,
      })
    )));
  }

  /**
   * Renames the given records.
   *
   * Display name only — the stored file is untouched, matching how Chat Documents
   * renames without moving anything in Storage.
   *
   * @param {Array<{productId: string, id: string}>} refs - The records to rename.
   * @param {string} fileName - The new display name.
   * @returns {Promise<void>} Resolves once the writes land.
   */
  static async renameMany(refs, fileName) {
    const db = getFirestore();

    await Promise.all(refs.map(({ productId, id }) => (
      db.doc(`${collectionPath(productId)}/${id}`).update({ fileName })
    )));
  }

  /**
   * Withdraws the given records from their products.
   *
   * Deletes the coach's records only — the stored file is the caller's to clean up,
   * because whether it is still needed depends on what was left behind. Removing
   * each client's copy, once a fan-out exists, is the server's job.
   *
   * @param {Array<{productId: string, id: string}>} refs - The records to remove.
   * @returns {Promise<void>} Resolves once the deletes land.
   */
  static async removeMany(refs) {
    const db = getFirestore();

    await Promise.all(refs.map(({ productId, id }) => (
      db.doc(`${collectionPath(productId)}/${id}`).delete()
    )));
  }
}

export default LibraryDoc;
